import { format, isValid, parse } from "date-fns";

import type {
  CreateTargetMarkerBody,
  MarkerAnchorBody,
  MarkerTypeApi,
  TargetMarker,
  UpdateTargetMarkerBody,
} from "api/types/markers";
import type {
  MarkerFormValues,
  MarkerTargetsContext,
  MarkerType,
} from "../Targets/MarkerDialog/MarkerDialog.types";

/** Form marker type → API marker type. Full `Record`s keep both directions exhaustive. */
const API_TYPE_BY_FORM_TYPE: Record<MarkerType, MarkerTypeApi> = {
  phaseChange: "phase_change",
  externalEvent: "external_event",
};

/** API marker type → form marker type. */
const FORM_TYPE_BY_API_TYPE: Record<MarkerTypeApi, MarkerType> = {
  phase_change: "phaseChange",
  external_event: "externalEvent",
};

/** Trims notes and turns a blank value into `null`, as the API stores it. */
const toApiNotes = (notes: string): string | null => notes.trim() || null;

/** Date format the markers API reads and writes (date-only). */
const API_DATE_FORMAT = "yyyy-MM-dd";

/**
 * Maps the form's chart position to the API's:
 * - "session": `sessionId` (a `target_session_id` string) → numeric `target_session_id`.
 * - "date": the picked calendar date → `marker_date` as `yyyy-MM-dd`, formatted
 *   from the local date so the day never shifts (never via `toISOString()`).
 *
 * @param values - Form values; the dialog only submits them with a valid position
 */
export function toMarkerAnchorBody(values: MarkerFormValues): MarkerAnchorBody {
  if (values.linkMode === "session") {
    const sessionId = Number(values.sessionId.trim() || Number.NaN);
    if (!Number.isInteger(sessionId) || sessionId <= 0) {
      throw new Error("A session-linked marker needs a valid sessionId");
    }
    return { target_session_id: sessionId };
  }
  if (!values.markerDate || !isValid(values.markerDate)) {
    throw new Error("A date-linked marker needs a valid markerDate");
  }
  return { marker_date: format(values.markerDate, API_DATE_FORMAT) };
}

/**
 * Parses an API `marker_date` into the local-midnight date the picker holds.
 * Only the calendar part is read: `new Date("2026-07-08T00:00:00.000Z")` would
 * show July 7th in time zones behind UTC.
 *
 * Returns null when the date is missing — e.g. from an API that predates marker
 * dates — so such a marker still opens, on its session.
 */
const toPickerDate = (markerDate: string | null | undefined): Date | null => {
  if (!markerDate) return null;
  const date = parse(markerDate.slice(0, 10), API_DATE_FORMAT, new Date());
  return isValid(date) ? date : null;
};

/**
 * Whether a marker can apply to more targets than the one it's added from.
 *
 * Only a date-linked external event can: session numbers differ between
 * targets, so a session-linked marker has no position on any other chart, and a
 * phase line marks a change in this target's own teaching procedures. The form
 * hides the section accordingly; this is the rule the payload is built on.
 *
 * @param values - Form values
 */
export const canShareMarker = (values: MarkerFormValues): boolean =>
  values.type === "externalEvent" && values.linkMode === "date";

/**
 * Targets a marker applies to: always the current target, plus the other
 * targets of its goal and/or the targets picked from other goals when those
 * options are on, plus the ones an edit carries over untouched
 * (`extraTargetIds`). A marker that can't be shared (see
 * {@link canShareMarker}) applies to the current target alone. Ascending,
 * without duplicates.
 *
 * @param values  - Form values
 * @param context - Goal options and the current goal/target
 */
export function resolveMarkerTargetIds(
  values: MarkerFormValues,
  context: MarkerTargetsContext
): number[] {
  const ids = new Set([context.currentTargetId]);

  if (canShareMarker(values)) {
    if (values.applyToCurrentGoal) {
      const currentGoal = context.goalOptions.find((goal) => goal.id === context.currentGoalId);
      currentGoal?.targets.forEach((target) => ids.add(target.id));
    }
    // Targets picked before a refresh may be gone (archived or deleted); the
    // backend would reject them, so only keep the ones still on offer. Same for
    // the targets an edit carries over untouched.
    const available = new Set(
      context.goalOptions.flatMap((goal) => goal.targets.map((target) => target.id))
    );
    if (values.applyToOtherGoals) {
      values.otherTargetIds.filter((id) => available.has(id)).forEach((id) => ids.add(id));
    }
    values.extraTargetIds.filter((id) => available.has(id)).forEach((id) => ids.add(id));
  }

  return Array.from(ids).sort((a, b) => a - b);
}

/** The form fields that describe which targets a marker applies to. */
type MarkerTargetSelection = Pick<
  MarkerFormValues,
  "applyToCurrentGoal" | "applyToOtherGoals" | "otherTargetIds" | "extraTargetIds"
>;

/** Nothing selected — the marker applies to the current target alone. */
const emptyTargetSelection = (): MarkerTargetSelection => ({
  applyToCurrentGoal: false,
  applyToOtherGoals: false,
  otherTargetIds: [],
  extraTargetIds: [],
});

/**
 * Reverse of {@link resolveMarkerTargetIds}: reads a persisted marker's
 * `target_ids` back into the form's checkboxes and picker.
 *
 * "All child targets within current clinical goal" is all-or-nothing, so it's
 * only ticked when every sibling target is covered — and never for a goal whose
 * only target is the current one, where it would mean nothing. Targets of the
 * current goal that a partial selection leaves out (possible after a "this
 * target only" delete) go to `extraTargetIds`, which the form doesn't show but
 * keeps, so an unrelated edit doesn't drop them.
 *
 * Without a context the selection can't be resolved and comes back empty.
 *
 * @param targetIds - Targets the marker applies to
 * @param [context] - Goal options and the current goal/target
 */
function toTargetSelection(
  targetIds: number[],
  context?: MarkerTargetsContext
): MarkerTargetSelection {
  if (!context || targetIds.length === 0) return emptyTargetSelection();

  const applied = new Set(targetIds);
  const currentGoal = context.goalOptions.find((goal) => goal.id === context.currentGoalId);
  const siblingIds = (currentGoal?.targets ?? [])
    .map((target) => target.id)
    .filter((id) => id !== context.currentTargetId);
  const applyToCurrentGoal = siblingIds.length > 0 && siblingIds.every((id) => applied.has(id));

  const otherGoalIds = new Set(
    context.goalOptions
      .filter((goal) => goal.id !== context.currentGoalId)
      .flatMap((goal) => goal.targets.map((target) => target.id))
  );
  const otherTargetIds = targetIds.filter((id) => otherGoalIds.has(id));

  return {
    applyToCurrentGoal,
    applyToOtherGoals: otherTargetIds.length > 0,
    otherTargetIds,
    // Unknown ids are left out: they're archived or deleted targets the backend
    // would reject anyway.
    extraTargetIds: applyToCurrentGoal ? [] : siblingIds.filter((id) => applied.has(id)),
  };
}

/**
 * Maps the MarkerDialog form values to the API create body, bridging the FE
 * camelCase / dialog enums to the backend's snake_case contract:
 *
 * - `phaseChange` → `phase_change`, `externalEvent` → `external_event`.
 * - The chart position (see {@link toMarkerAnchorBody}).
 * - `target_ids` only when the marker is shared with more targets; otherwise the
 *   backend applies it to the route target.
 * - `notes` is trimmed; blank becomes `null`.
 *
 * The dialog always requires a non-empty label, so it's always sent. The form
 * no longer collects a transition target — clinicians note the phase in the
 * label if it applies — so `transition_to` is never sent.
 */
export function toCreateMarkerBody(
  values: MarkerFormValues,
  targetIds: number[] = []
): CreateTargetMarkerBody {
  return {
    ...toMarkerAnchorBody(values),
    ...(targetIds.length > 1 && { target_ids: targetIds }),
    marker_type: API_TYPE_BY_FORM_TYPE[values.type],
    label: values.label.trim(),
    notes: toApiNotes(values.notes),
  };
}

/**
 * Maps a persisted marker to the values the MarkerDialog edits it from — the
 * reverse of {@link toCreateMarkerBody}. A marker created from a session opens
 * linked to it; date-based and shared markers open on their date. `notes` comes
 * back as `null` when unset and becomes an empty field; "create more" never
 * applies to an edit.
 *
 * With a targets context the marker's `target_ids` are read back into the
 * additional-targets section (see {@link toTargetSelection}).
 *
 * @param marker - The persisted marker being edited
 * @param [context] - Goal options and the current goal/target
 */
export function toMarkerFormValues(
  marker: TargetMarker,
  context?: MarkerTargetsContext
): MarkerFormValues {
  return {
    type: FORM_TYPE_BY_API_TYPE[marker.marker_type],
    linkMode: marker.target_session_id == null ? "date" : "session",
    // Date-based and shared markers have no session to preselect.
    sessionId: marker.target_session_id == null ? "" : String(marker.target_session_id),
    markerDate: toPickerDate(marker.marker_date),
    label: marker.label,
    notes: marker.notes ?? "",
    ...toTargetSelection(marker.target_ids ?? [], context),
    createMore: false,
  };
}

/**
 * Maps edited form values to the API update body: the chart position (see
 * {@link toMarkerAnchorBody}), `label` and `notes`, plus the targets the marker
 * applies to when they're known. The marker's type can't change, and leaving
 * `transition_to` out keeps whatever the backend already stores.
 *
 * `target_ids` replaces the whole list, so it's sent whenever it was resolved —
 * that's what lets an edit drop a target — and left out otherwise, which keeps
 * the stored list.
 *
 * @param values - The edited form values
 * @param [targetIds] - Targets the marker should apply to, including the current one
 */
export function toUpdateMarkerBody(
  values: MarkerFormValues,
  targetIds?: number[]
): UpdateTargetMarkerBody {
  return {
    ...toMarkerAnchorBody(values),
    ...(targetIds?.length && { target_ids: targetIds }),
    label: values.label.trim(),
    notes: toApiNotes(values.notes),
  };
}
